/** Shared pieces: icons, the mark, fields, data loading. */
import { useCallback, useEffect, useState, type ReactNode } from 'react';

const ICONS: Record<string, ReactNode> = {
  schedule: <path d="M2.5 3.5h11v10h-11zM2.5 6.5h11M5.5 2v3M10.5 2v3" />,
  calendar: <path d="M2.5 3.5h11v10h-11zM2.5 6.5h11M5.5 2v3M10.5 2v3M5 9h1.5M8 9h1.5M5 11.25h1.5" />,
  policies: <path d="M4 1.75h6.5L13 4.25v10H4zM10.25 1.75v2.75H13M6.25 7.5h4.5M6.25 10h4.5M6.25 12.5h2.5" />,
  settings: (
    <>
      <circle cx="8" cy="8" r="2.25" />
      <path d="M8 1.75v2M8 12.25v2M1.75 8h2M12.25 8h2M3.6 3.6L5 5M11 11l1.4 1.4M3.6 12.4L5 11M11 5l1.4-1.4" />
    </>
  ),
  menu: <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />,
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  back: <path d="M10 3.5L5.5 8l4.5 4.5" />,
  next: <path d="M6 3.5L10.5 8 6 12.5" />,
  plus: <path d="M8 3v10M3 8h10" />,
  key: <path d="M10 2.5a3.5 3.5 0 1 1-2.9 5.46L2.5 12.5V14h2v-1.5H6V11l1.6-1.6A3.5 3.5 0 0 1 10 2.5zM10.75 5.25h.01" />,
  out: <path d="M6.5 2.5h-4v11h4M10 5l3 3-3 3M13 8H6" />,
  check: <path d="M3.5 8.5l3 3 6-7" />,
};

export function Icon({ name, size = 16 }: { name: keyof typeof ICONS | string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

/** The Kept mark: a filed sheet over its carbon copy, two lines struck in carbon. */
export function Mark({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.5 2.75h11.75v15.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <rect x="3.75" y="5.75" width="12.5" height="15.5" rx="1.25" fill="var(--surface)" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6.75 10h6.5M6.75 13.25h6.5" stroke="var(--carbon)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.75 16.5h3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function Brand({ size = 24 }: { size?: number }) {
  return (
    <div className="brand">
      <Mark size={size} />
      <span className="brand-name">Kept</span>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

export function ErrorNote({ error }: { error: string | null }) {
  return error ? (
    <p className="error" role="alert">
      {error}
    </p>
  ) : null;
}

/** Loads data once and on reload(); exposes loading and error states. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  const run = useCallback(load, deps);
  const reload = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    run().then(
      (data) => setState({ data, error: null, loading: false }),
      (e: Error) => setState({ data: null, error: e.message, loading: false }),
    );
  }, [run]);
  useEffect(reload, [reload]);
  return { ...state, reload };
}

/** Runs an async action with a busy flag and an error message. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, setError, run };
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-head">
          <h2>{title}</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
